import { ServicesState, ServicesStatus, BusinessAreaServicesState } from './ServicesState';
import { BusinessAreaType } from 'model/Service';
import { getBoardingAppliedOfferForStandardPricing } from 'components/CreateBooking/Boarding/utils/boardingPricing';
import {
    SERVICES_RESET_ACTION_TYPE,
    SERVICES_CLEAR_ACTION_TYPE,
    SERVICES_FETCH_START_ACTION_TYPE,
    SERVICES_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_FETCH_EVALUATION_START_ACTION_TYPE,
    SERVICES_FETCH_EVALUATION_SUCCESS_ACTION_TYPE,
    SERVICES_SAVE_START_ACTION_TYPE,
    SERVICES_SAVE_SUCCESS_ACTION_TYPE,
    SERVICES_DELETE_START_ACTION_TYPE,
    SERVICES_DELETE_SUCCESS_ACTION_TYPE,
    SERVICES_ACTIVATE_START_ACTION_TYPE,
    SERVICES_ACTIVATE_SUCCESS_ACTION_TYPE,
    SERVICES_INACTIVATE_START_ACTION_TYPE,
    SERVICES_INACTIVATE_SUCCESS_ACTION_TYPE,
    SERVICES_PET_TYPES_UPDATE_START_ACTION_TYPE,
    SERVICES_PET_TYPES_UPDATE_SUCCESS_ACTION_TYPE,
    SERVICES_BUSINESS_AREA_UPDATE_START_ACTION_TYPE,
    SERVICES_BUSINESS_AREA_UPDATE_SUCCESS_ACTION_TYPE,
    SERVICES_STAFF_UPDATE_START_ACTION_TYPE,
    SERVICES_STAFF_UPDATE_SUCCESS_ACTION_TYPE,
    SERVICES_DAYCARE_FETCH_START_ACTION_TYPE,
    SERVICES_DAYCARE_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_ERROR_ACTION_TYPE,
    ServicesAction,
    SERVICES_BOARDING_FETCH_START_ACTION_TYPE,
    SERVICES_BOARDING_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_TRANSPORTATION_FETCH_START_ACTION_TYPE,
    SERVICES_TRANSPORTATION_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_TRAINING_FETCH_START_ACTION_TYPE,
    SERVICES_TRAINING_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_WALKING_FETCH_START_ACTION_TYPE,
    SERVICES_WALKING_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_BOARDING_OFFERS_FETCH_START_ACTION_TYPE,
    SERVICES_BOARDING_OFFERS_FETCH_SUCCESS_ACTION_TYPE,
    SERVICES_SAVE_BULK_START_ACTION_TYPE,
    SERVICES_SAVE_BULK_SUCCESS_ACTION_TYPE
} from 'actions/services/ServicesActionsTypes';
import { Reducer } from 'redux';

const createInitialBusinessAreaState = (): BusinessAreaServicesState => ({
    services: [],
    meta: null,
    loading: false
});

// EVOLVED: Initial state with segmented architecture
const initialState: ServicesState = {
    servicesByBusinessArea: {
        grooming: createInitialBusinessAreaState(),
        dental: createInitialBusinessAreaState(),
        daycare: createInitialBusinessAreaState(),
        boarding: createInitialBusinessAreaState(),
        transportation: createInitialBusinessAreaState(),
        evaluation: createInitialBusinessAreaState(),
        training: createInitialBusinessAreaState(),
        walking: createInitialBusinessAreaState()
    },
    status: ServicesStatus.Initial,
    currentBusinessArea: 'grooming', // Default for backwards compatibility

    // BACKWARDS COMPATIBILITY: Legacy fields initialized from grooming area
    services: [],
    meta: null,
    loading: false
};

// Helper to extract business area from action payload
const getBusinessAreaFromAction = (action: any): BusinessAreaType => {
    return action.payload?.businessArea || 'grooming'; // Default to grooming for backwards compatibility
};

// Helper to sync legacy fields with current business area
const syncLegacyFields = (state: ServicesState): ServicesState => {
    const currentAreaState = state.servicesByBusinessArea[state.currentBusinessArea];
    return {
        ...state,
        // BACKWARDS COMPATIBILITY: Sync legacy fields with current business area
        services: currentAreaState.services,
        meta: currentAreaState.meta,
        loading: currentAreaState.loading
    };
};

export const servicesReducer: Reducer<ServicesState, ServicesAction> = (state = initialState, action) => {
    switch (action.type) {
        case SERVICES_RESET_ACTION_TYPE:
            return {
                ...initialState
            };
        case SERVICES_CLEAR_ACTION_TYPE: {
            const newState = {
                ...state,
                status: ServicesStatus.Initial,
                servicesByBusinessArea: {
                    ...state.servicesByBusinessArea,
                    [state.currentBusinessArea]: createInitialBusinessAreaState()
                }
            };
            return syncLegacyFields(newState);
        }
        case SERVICES_FETCH_START_ACTION_TYPE: {
            const businessArea = getBusinessAreaFromAction(action);
            const newState = {
                ...state,
                status: ServicesStatus.Fetching,
                currentBusinessArea: businessArea,
                servicesByBusinessArea: {
                    ...state.servicesByBusinessArea,
                    [businessArea]: {
                        ...state.servicesByBusinessArea[businessArea],
                        loading: true
                    }
                }
            };
            return syncLegacyFields(newState);
        }
        case SERVICES_FETCH_SUCCESS_ACTION_TYPE: {
            const businessArea = getBusinessAreaFromAction(action);
            const newState = {
                ...state,
                status: ServicesStatus.FetchSuccess,
                currentBusinessArea: businessArea,
                duplicatedServiceId: action.payload.duplicatedId,
                servicesByBusinessArea: {
                    ...state.servicesByBusinessArea,
                    [businessArea]: {
                        services: action.payload.services,
                        meta: action.payload.meta,
                        loading: false
                    }
                }
            };
            return syncLegacyFields(newState);
        }
        case SERVICES_FETCH_EVALUATION_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.FetchingEvaluation,
                loading: true
            };
        case SERVICES_FETCH_EVALUATION_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.FetchEvaluationSuccess,
                evaluationService: action.payload.evaluationService,
                loading: false
            };
        case SERVICES_SAVE_START_ACTION_TYPE: {
            return {
                ...state,
                status: ServicesStatus.Saving,
                savedService: undefined
                // Note: We don't update loading in business areas for save operations
                // as saves don't typically require business area context
            };
        }
        case SERVICES_SAVE_SUCCESS_ACTION_TYPE: {
            const savedService = action.payload.service;
            const serviceBusinessArea = (savedService.businessArea?.id as BusinessAreaType) || 'grooming';

            // Update the service in the correct business area
            const currentAreaServices = state.servicesByBusinessArea[serviceBusinessArea].services;
            const serviceExists = currentAreaServices.some(service => service.uuid === savedService.uuid);

            const updatedServices = serviceExists
                ? currentAreaServices.map(service =>
                      service.uuid === savedService.uuid ? { ...savedService } : service
                  )
                : [...currentAreaServices, { ...savedService }];

            const newState = {
                ...state,
                status: ServicesStatus.SaveSuccess,
                savedService: { ...savedService },
                servicesByBusinessArea: {
                    ...state.servicesByBusinessArea,
                    [serviceBusinessArea]: {
                        ...state.servicesByBusinessArea[serviceBusinessArea],
                        services: updatedServices
                    }
                }
            };
            return syncLegacyFields(newState);
        }
        case SERVICES_SAVE_BULK_START_ACTION_TYPE: {
            return {
                ...state,
                status: ServicesStatus.SavingBulk,
                savedService: undefined
            };
        }
        case SERVICES_SAVE_BULK_SUCCESS_ACTION_TYPE: {
            return {
                ...state,
                status: ServicesStatus.SaveBulkSuccess
            };
        }
        case SERVICES_DELETE_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.Deleting
            };
        case SERVICES_DELETE_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.DeleteSuccess
            };
        case SERVICES_ACTIVATE_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.Activating
            };
        case SERVICES_ACTIVATE_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.ActivateSuccess
            };
        case SERVICES_INACTIVATE_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.Inactivating
            };
        case SERVICES_INACTIVATE_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.InactivateSuccess
            };
        case SERVICES_PET_TYPES_UPDATE_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.PetTypesUpdating
            };
        case SERVICES_PET_TYPES_UPDATE_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.PetTypesUpdateSuccess
            };
        case SERVICES_BUSINESS_AREA_UPDATE_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.BusinessAreaUpdating
            };
        case SERVICES_BUSINESS_AREA_UPDATE_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.BusinessAreaUpdateSuccess
            };
        case SERVICES_STAFF_UPDATE_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.StaffUpdating
            };
        case SERVICES_STAFF_UPDATE_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.StaffUpdateSuccess
            };
        case SERVICES_DAYCARE_FETCH_START_ACTION_TYPE: {
            return {
                ...state,
                // BACKWARDS COMPATIBILITY: Sync legacy fields with current business area
                status: ServicesStatus.FetchingDaycareServices
            };
        }
        case SERVICES_DAYCARE_FETCH_SUCCESS_ACTION_TYPE: {
            return {
                ...state,
                status: ServicesStatus.FetchDaycareServicesSuccess,
                daycareServices: action.payload.daycareServices
            };
        }
        case SERVICES_BOARDING_FETCH_START_ACTION_TYPE: {
            return {
                ...state,
                // BACKWARDS COMPATIBILITY: Sync legacy fields with current business area
                status: ServicesStatus.FetchingBoardingServices
            };
        }
        case SERVICES_BOARDING_FETCH_SUCCESS_ACTION_TYPE: {
            return {
                ...state,
                status: ServicesStatus.FetchBoardingServicesSuccess,
                boardingServices: action.payload.boardingServices.map(service => ({
                    ...service,
                    catalogPrice: service.catalogPrice ?? service.price
                }))
            };
        }
        case SERVICES_BOARDING_OFFERS_FETCH_START_ACTION_TYPE: {
            return {
                ...state,
                status: ServicesStatus.FetchingBoardingOffers
            };
        }
        case SERVICES_BOARDING_OFFERS_FETCH_SUCCESS_ACTION_TYPE: {
            const prevById = new Map((state.boardingServices ?? []).map(service => [service.serviceId, service]));
            const boardingServices = action.payload.boardingServices.map(next => {
                const prev = prevById.get(next.serviceId);
                const standardAppliedOffer = getBoardingAppliedOfferForStandardPricing(next.appliedOffer);
                const appliedNightly = standardAppliedOffer?.pricePerNight;
                if (!prev) {
                    const catalogPrice = next.catalogPrice ?? next.price;
                    return {
                        ...next,
                        catalogPrice,
                        appliedOffer: standardAppliedOffer,
                        price:
                            appliedNightly != null && Number.isFinite(appliedNightly)
                                ? appliedNightly
                                : next.price ?? catalogPrice
                    };
                }
                const catalogPrice = next.catalogPrice ?? prev.catalogPrice ?? prev.price;
                return {
                    ...next,
                    catalogPrice,
                    appliedOffer: standardAppliedOffer,
                    price:
                        appliedNightly != null && Number.isFinite(appliedNightly)
                            ? appliedNightly
                            : next.price ?? catalogPrice
                };
            });
            return {
                ...state,
                status: ServicesStatus.FetchBoardingOffersSuccess,
                boardingServices
            };
        }
        case SERVICES_TRANSPORTATION_FETCH_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.FetchingTransportationServices
            };

        case SERVICES_TRANSPORTATION_FETCH_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                transportationServices: action.payload.transportationServices,
                status: ServicesStatus.FetchTransportationServicesSuccess
            };

        case SERVICES_TRAINING_FETCH_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.FetchingTrainingServices
            };

        case SERVICES_TRAINING_FETCH_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                trainingServices: action.payload.trainingServices,
                status: ServicesStatus.FetchTrainingServicesSuccess
            };

        case SERVICES_WALKING_FETCH_START_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.FetchingWalkingServices
            };

        case SERVICES_WALKING_FETCH_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                walkingServices: action.payload.walkingServices,
                status: ServicesStatus.FetchWalkingServicesSuccess
            };

        case SERVICES_ERROR_ACTION_TYPE:
            return {
                ...state,
                status: ServicesStatus.Error
            };
        default:
            return state;
    }
};

export default servicesReducer;
